import {isPlatformBrowser} from '@angular/common';
import {inject, Injector, Injectable, PLATFORM_ID} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {ConversationApiService} from '@shared/services/api/conversation/conversation-api.service';
import {ConversationListService} from './conversation-list.service';
import {IConversation} from '../models/conversation.model';
import {CHANNEL} from '../models/channel.model';
import {ExtensionSendService} from './extension-send.service';

interface IMissingAvatarSyncCacheEntry {
  avatar: string;
  /** Tên tại lúc đồng bộ không ra tên thật; entry đời cũ (chỉ cache avatar) không có trường này. */
  name?: string;
  expiresAt: number;
  pageId?: string;
}

/**
 * Kênh BE `updatecustomer?syncName` lấy được tên/avatar từ nền tảng. Kênh khác (Shopee, TikTok Shop, Telegram,
 * WhatsApp…) BE không có nhánh đồng bộ nhưng vẫn ghi customer; Lazada cần sessionId mà luồng tự động không có.
 */
const AUTO_SYNC_CHANNELS: ReadonlySet<number> = new Set([
  CHANNEL.FACEBOOK, CHANNEL.INSTAGRAM, CHANNEL.ZALO, CHANNEL.ZALO_PERSONAL, CHANNEL.TIKTOK_BUSINESS, CHANNEL.THREADS,
]);
/** FE13 chỉ tự đồng bộ 50 hội thoại đầu sau mỗi lần tải danh sách. */
const LIST_AUTO_SYNC_LIMIT = 50;
/** Avatar lỗi vừa nhờ BE lấy lại mà BE trả về chính URL lỗi/không có → chờ một lúc mới thử lại cùng URL. */
const BROKEN_AVATAR_RETRY_DELAY = 10 * 60 * 1000;

/**
 * Service đồng bộ thông tin khách hàng (tên + avatar) từ nền tảng.
 *
 * Flow:
 * 1. Phát hiện tên mặc định ("Facebook User", "Zalo User"...)
 *    hoặc avatar lỗi (403/4xx) → gọi API updateCustomer(syncName: 1)
 * 2. BE fetch tên/avatar thật từ nền tảng (FB, Zalo...)
 * 3. Cập nhật vào conversation list qua ConversationListService
 * 4. List re-render → UI hiển thị tên/avatar thật
 *
 * Chỉ hỗ trợ kênh Facebook và Zalo cá nhân (BE side).
 * Gọi API lần lượt bằng await để tránh áp lực BE.
 */
@Injectable({providedIn: 'root'})
export class CustomerSyncService {
  private readonly api = inject(ConversationApiService);
  private readonly extensionSend = inject(ExtensionSendService);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);
  private _convListSvc: ConversationListService | null = null;

  /** Lazy get ConversationListService — tránh circular dependency NG0200 */
  private get convListSvc(): ConversationListService {
    if (!this._convListSvc) {
      this._convListSvc = this.injector.get(ConversationListService);
    }
    return this._convListSvc;
  }

  /** Track các conversation đang sync — tránh gọi trùng lặp */
  private _syncing = new Set<string>();
  /** Avatar lỗi có thể xuất hiện ở nhiều vị trí cùng lúc (sidebar, header); chỉ refresh một lần. */
  private readonly _refreshingBrokenAvatars = new Map<string, Promise<string | null>>();
  /**
   * Cache âm cho avatar placeholder/null. Nếu BE vừa sync nhưng vẫn không có
   * avatar thì giữ kết quả đó một ngày, tránh mỗi F5 lại gọi updatecustomer.
   */
  private readonly missingAvatarSyncCacheKey = 'vpage.customer-missing-avatar-sync.v1';
  private readonly missingAvatarSyncTtl = 12 * 60 * 60 * 1000;
  private missingAvatarSyncCache: Record<string, IMissingAvatarSyncCacheEntry> | null = null;

  /** Tên mặc định từ BE khi API nền tảng không lấy được tên thật */
  static readonly DEFAULT_CUSTOMER_NAMES = new Set([
    'Facebook User',
    'Instagram User',
    'Shopee User',
    'Zalo User',
    'Lazada User',
    'Telegram User',
    'WhatsApp User',
    'Whatsapp User',
    'TikTok User',
    'Zalo Personal User',
  ]);

  // ── Detection helpers ─────────────────────────────────────────────

  /** Kiểm tra tên khách có phải là tên mặc định (chưa sync) không */
  isDefaultCustomerName(name: string | null | undefined): boolean {
    if (!name) return false;
    return CustomerSyncService.DEFAULT_CUSTOMER_NAMES.has(name.trim());
  }

  /** Kiểm tra URL avatar có phải là avatar thật (không phải default placeholder) không */
  isRealAvatar(url: string | null | undefined): boolean {
    if (!url) return false;
    return !url.includes('default_avatar') && url.length > 0;
  }

  /**
   * Thiếu tên thật hoặc avatar thật đều cần nhờ BE đồng bộ lại từ platform.
   * Không dùng mỗi tên mặc định: webhook thường đã có tên nhưng vẫn giữ
   * `default_avatar.png` trong DB.
   */
  needsCustomerDataSync(conv: Pick<IConversation, 'id' | 'fanpageUserName' | 'customerAvatar'>): boolean {
    const needsSync = this.isDefaultCustomerName(conv.fanpageUserName)
      || !this.isRealAvatar(conv.customerAvatar);
    return needsSync && !this.isMissingAvatarSyncCached(conv.id, conv.customerAvatar, conv.fanpageUserName);
  }

  /** Luồng tự động (sau khi tải danh sách, hội thoại mới) chỉ gọi cho kênh BE đồng bộ được. */
  private canAutoSync(conv: Pick<IConversation, 'channel'>): boolean {
    return conv.channel == null || AUTO_SYNC_CHANNELS.has(Number(conv.channel));
  }

  // ── Sync methods ─────────────────────────────────────────────────

  /**
   * Sync thông tin khách hàng (tên + avatar) cho 1 hội thoại.
   * Gọi API updateCustomer(syncName: 1) — BE fetch tên/avatar thật từ nền tảng.
   * Kết quả được cập nhật trực tiếp vào conversation list qua updateConversationInList.
   *
   * @returns { name, avatar } nếu có cập nhật, null nếu không cần sync hoặc lỗi
   */
  async syncCustomerData(
    conv: { id: string; fanpageUserId?: string; fanpageId?: string },
    options?: { originalName?: string },
  ): Promise<{ name?: string; avatar?: string } | null> {
    if (!conv.fanpageUserId || !conv.fanpageId || this._syncing.has(conv.id)) return null;

    this._syncing.add(conv.id);
    let resolvedAvatar = this.toNonEmptyString((conv as {customerAvatar?: unknown}).customerAvatar);
    let resolvedName = this.toNonEmptyString((conv as {fanpageUserName?: unknown}).fanpageUserName);
    try {
      const params: Record<string, unknown> = {
        id: conv.id,
        syncName: 1,
        fanpageUserId: conv.fanpageUserId,
        fanpageId: conv.fanpageId,
      };
      if (options?.originalName?.trim()) {
        params['originalName'] = options.originalName.trim();
      }

      const res = await firstValueFrom(this.api.updateCustomer(params));
      // BE trả `{error}` (VD 'No data!' khi Graph không đọc được hồ sơ khách) → không có gì được cập nhật.
      if (res?.code === 0) return null;

      // BE có thể trả trực tiếp `{item, avatar}` hoặc bọc trong `data`.
      // `avatar` cũng có thể là object Graph API (`avatar.data.url`), không
      // phải chuỗi URL trực tiếp.
      const data = (res as any)?.data ?? res;
      const item = data?.item ?? data?.customer ?? data;
      const newName = this.toNonEmptyString(
        item?.name ?? data?.name ?? item?.fanpageUserName ?? data?.fanpageUserName,
      );
      const newAvatar = this.toAvatarUrl(
        item?.avatar ?? data?.avatar ?? item?.customerAvatar ?? data?.customerAvatar,
      );
      resolvedAvatar = newAvatar ?? resolvedAvatar;
      resolvedName = newName ?? resolvedName;

      const updates: Partial<IConversation> = {};
      if (newName && typeof newName === 'string' && newName.trim()) {
        updates.fanpageUserName = newName.trim();
      }
      if (newAvatar && typeof newAvatar === 'string' && newAvatar.trim()) {
        updates.customerAvatar = newAvatar.trim();
      }

      if (Object.keys(updates).length > 0) {
        this.convListSvc.updateConversationInList(conv.id, updates);
      }

      return {
        name: updates.fanpageUserName,
        avatar: updates.customerAvatar,
      };
    } catch {
      return null;
    } finally {
      // Cache âm khi đồng bộ xong vẫn còn avatar placeholder/null hoặc tên mặc định (BE không lấy được từ nền tảng),
      // để mỗi lần tải danh sách không gọi lại cho cùng hội thoại. Avatar thật bị lỗi tải ảnh đi luồng syncBrokenAvatar.
      if (this.isRealAvatar(resolvedAvatar) && !this.isDefaultCustomerName(resolvedName)) {
        this.clearMissingAvatarSyncCache(conv.id);
      } else {
        this.cacheMissingAvatarSync(conv.id, resolvedAvatar, conv.fanpageId, resolvedName);
      }
      this._syncing.delete(conv.id);
    }
  }

  /** Lượt tải danh sách mới nhất: tải lại (đổi lọc, F5 danh sách) thì dừng chuỗi đồng bộ của lượt cũ. */
  private listSyncGeneration = 0;
  /** Hội thoại mới đến lúc tab đang ẩn: chờ người dùng quay lại tab mới đồng bộ. */
  private readonly pendingHiddenSyncs = new Map<string, IConversation>();
  private visibilityListenerAttached = false;

  /**
   * Batch sync tên/avatar placeholder sau khi tải danh sách — gọi lần lượt bằng await.
   * Như FE13: tối đa LIST_AUTO_SYNC_LIMIT hội thoại mỗi lượt, chỉ kênh BE đồng bộ được.
   * @param freshLoad tải mới danh sách (không phải tải thêm) → dừng chuỗi của lượt trước.
   */
  async syncDefaultNames(convs: IConversation[], freshLoad = false): Promise<void> {
    const generation = freshLoad ? ++this.listSyncGeneration : this.listSyncGeneration;
    let synced = 0;
    for (const conv of convs) {
      if (generation !== this.listSyncGeneration || synced >= LIST_AUTO_SYNC_LIMIT) return;
      if (!this.canAutoSync(conv) || !this.needsCustomerDataSync(conv)) continue;
      synced++;
      await this.syncCustomerData(conv);
    }
  }

  /**
   * Hội thoại mới vào danh sách qua realtime có tên/avatar placeholder. Mọi tab của mọi nhân viên đều nhận sự kiện,
   * nên tab đang ẩn để dành tới khi người dùng quay lại tab (lúc đó mới cần thấy tên thật).
   */
  syncNewConversation(conv: IConversation): void {
    if (!this.canAutoSync(conv) || !this.needsCustomerDataSync(conv)) return;
    if (isPlatformBrowser(this.platformId) && document.visibilityState === 'hidden') {
      this.pendingHiddenSyncs.set(String(conv.id), conv);
      this.attachVisibilityListener();
      return;
    }
    void this.syncCustomerData(conv);
  }

  private attachVisibilityListener(): void {
    if (this.visibilityListenerAttached) return;
    this.visibilityListenerAttached = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || !this.pendingHiddenSyncs.size) return;
      const pending = [...this.pendingHiddenSyncs.values()];
      this.pendingHiddenSyncs.clear();
      // Lấy bản mới nhất trong danh sách: có thể đã được đồng bộ ở lượt tải khác hoặc đã rời danh sách.
      const items = new Map(this.convListSvc.getCurrentItems().map(item => [String(item.id), item]));
      void this.syncDefaultNames(pending
        .map(conv => items.get(String(conv.id)))
        .filter((item): item is IConversation => !!item));
    });
  }

  private toNonEmptyString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private toAvatarUrl(value: unknown): string | null {
    if (typeof value === 'string') return this.toNonEmptyString(value);
    if (!value || typeof value !== 'object') return null;

    const avatar = value as Record<string, unknown>;
    return this.toNonEmptyString(avatar['url'])
      ?? this.toNonEmptyString((avatar['data'] as Record<string, unknown> | undefined)?.['url']);
  }

  /**
   * Khi người dùng chủ động truy cập/chọn Page, bỏ cache âm của Page đó một
   * lần để có cơ hội lấy avatar mới. Cache các Page khác vẫn được giữ nguyên.
   */
  resetMissingAvatarSyncCacheForPages(pageIds: Array<string | number>): void {
    const selectedPageIds = new Set(pageIds.map(id => String(id)).filter(Boolean));
    if (!selectedPageIds.size) return;

    const cache = this.getMissingAvatarSyncCache();
    let changed = false;
    Object.entries(cache).forEach(([conversationId, entry]) => {
      // Entry đời cũ chưa có pageId cũng được bỏ ở lần chuyển Page đầu tiên;
      // như vậy không để cache legacy chặn cơ hội sync mới.
      if (!entry.pageId || selectedPageIds.has(entry.pageId)) {
        delete cache[conversationId];
        changed = true;
      }
    });
    if (changed) this.persistMissingAvatarSyncCache();
  }

  private isMissingAvatarSyncCached(
    conversationId: string | undefined,
    avatar: string | null | undefined,
    name?: string | null,
  ): boolean {
    if (!conversationId) return false;
    const cache = this.getMissingAvatarSyncCache();
    const entry = cache[conversationId];
    if (!entry) return false;

    if (entry.expiresAt <= Date.now()) {
      delete cache[conversationId];
      this.persistMissingAvatarSyncCache();
      return false;
    }

    // Cache gắn với cả conversation lẫn giá trị avatar/tên tại lúc gọi. Nếu BE/extension đã thay một trong hai,
    // cho phép thử đồng bộ lại. Entry đời cũ không lưu tên → chỉ so avatar như trước.
    return entry.avatar === this.normalizeAvatarForCache(avatar)
      && (entry.name === undefined || entry.name === this.normalizeAvatarForCache(name));
  }

  private cacheMissingAvatarSync(
    conversationId: string,
    avatar: string | null | undefined,
    pageId?: string,
    name?: string | null,
  ): void {
    if (!conversationId) return;
    const cache = this.getMissingAvatarSyncCache();
    cache[conversationId] = {
      avatar: this.normalizeAvatarForCache(avatar),
      name: this.normalizeAvatarForCache(name),
      expiresAt: Date.now() + this.missingAvatarSyncTtl,
      pageId: pageId ? String(pageId) : undefined,
    };
    this.trimMissingAvatarSyncCache(cache);
    this.persistMissingAvatarSyncCache();
  }

  private clearMissingAvatarSyncCache(conversationId: string): void {
    const cache = this.getMissingAvatarSyncCache();
    if (!cache[conversationId]) return;
    delete cache[conversationId];
    this.persistMissingAvatarSyncCache();
  }

  private getMissingAvatarSyncCache(): Record<string, IMissingAvatarSyncCacheEntry> {
    if (this.missingAvatarSyncCache) return this.missingAvatarSyncCache;

    this.missingAvatarSyncCache = {};
    if (!isPlatformBrowser(this.platformId)) return this.missingAvatarSyncCache;
    try {
      const parsed = JSON.parse(localStorage.getItem(this.missingAvatarSyncCacheKey) || '{}') as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        Object.entries(parsed).forEach(([conversationId, value]) => {
          const item = value as {avatar?: unknown; name?: unknown; expiresAt?: unknown; pageId?: unknown};
          const expiresAt = Number(item?.expiresAt);
          if (conversationId && Number.isFinite(expiresAt) && expiresAt > Date.now()) {
            this.missingAvatarSyncCache![conversationId] = {
              avatar: this.normalizeAvatarForCache(item?.avatar),
              ...(typeof item?.name === 'string' ? {name: item.name} : {}),
              expiresAt,
              pageId: typeof item?.pageId === 'string' && item.pageId ? item.pageId : undefined,
            };
          }
        });
      }
    } catch {
      // Local storage hỏng/đầy không được phép chặn việc mở hội thoại.
    }
    return this.missingAvatarSyncCache;
  }

  private persistMissingAvatarSyncCache(): void {
    if (!isPlatformBrowser(this.platformId) || !this.missingAvatarSyncCache) return;
    try {
      localStorage.setItem(this.missingAvatarSyncCacheKey, JSON.stringify(this.missingAvatarSyncCache));
    } catch {
      // Cache là best-effort; không ảnh hưởng luồng đồng bộ chính.
    }
  }

  private trimMissingAvatarSyncCache(cache: Record<string, IMissingAvatarSyncCacheEntry>): void {
    const entries = Object.entries(cache)
      .sort(([, left], [, right]) => left.expiresAt - right.expiresAt);
    // Giới hạn cache để localStorage không phình theo lịch sử hội thoại dài.
    while (entries.length > 500) {
      const [conversationId] = entries.shift()!;
      delete cache[conversationId];
    }
  }

  private normalizeAvatarForCache(avatar: unknown): string {
    return typeof avatar === 'string' ? avatar.trim() : '';
  }

  /**
   * Sync avatar cho 1 hội thoại bị lỗi (403/4xx).
   * Gọi khi image error event fired trên avatar ở sidebar hoặc header hội thoại; các nơi gọi
   * cùng lúc cho một hội thoại dùng chung một request.
   * Instagram cần extension quét lại globalUserId/avatar vì avatar Meta CDN có hạn.
   *
   * @returns URL avatar mới khi BE trả về ngay; null khi không lấy được, kênh không hỗ trợ
   *          hoặc avatar được cập nhật sau qua extension (Instagram)
   */
  syncBrokenAvatar(
    conv: { id: string; channel?: number; fanpageUserId?: string; fanpageId?: string; fanpageUserName?: string; customerAvatar?: string | null }
  ): Promise<string | null> {
    // Chỉ sync nếu avatar là URL thật (không phải default placeholder)
    if (!this.isRealAvatar(conv.customerAvatar)) return Promise.resolve(null);
    const running = this._refreshingBrokenAvatars.get(conv.id);
    if (running) return running;
    // Dòng bị vẽ lại (virtual scroll, sắp xếp lại) với đúng URL vừa thử mà BE không lấy được ảnh mới → không gọi lại.
    const lastAttempt = this.brokenAvatarAttempts.get(this.brokenAvatarKey(conv));
    if (lastAttempt && Date.now() - lastAttempt < BROKEN_AVATAR_RETRY_DELAY) return Promise.resolve(null);

    // Instagram đi qua extension; các kênh còn lại chỉ FB, Zalo cá nhân, TikTok Business được BE hỗ trợ.
    const isInstagram = conv.channel === CHANNEL.INSTAGRAM;
    if (!isInstagram && ![CHANNEL.FACEBOOK, CHANNEL.ZALO_PERSONAL, CHANNEL.TIKTOK_BUSINESS].includes(conv.channel || -1)) {
      return Promise.resolve(null);
    }

    const request = isInstagram ? this.requestInstagramAvatar(conv) : this.syncAvatarFromPlatform(conv);
    this._refreshingBrokenAvatars.set(conv.id, request);
    return request;
  }

  /**
   * V1 không gọi BE để refresh avatar Instagram. Extension tra lại qua
   * Facebook Page liên kết, kèm forceRescan để nhận profilePicUrl mới.
   */
  private async requestInstagramAvatar(
    conv: { id: string; fanpageUserId?: string; fanpageId?: string; fanpageUserName?: string }
  ): Promise<null> {
    const requested = Boolean(conv.fanpageId && conv.fanpageUserId && await this.extensionSend.requestInstagramGlobalUserId({
      pageId: conv.fanpageId,
      recipientId: conv.fanpageUserId,
      username: conv.fanpageUserName,
      forceRescan: true,
    }));
    // Extension không có callback lỗi. Giữ khoá ngắn để tránh mỗi <img>
    // 403 phát thêm một request, rồi cho phép thử lại nếu extension im lặng.
    setTimeout(() => this._refreshingBrokenAvatars.delete(conv.id), requested ? 30_000 : 0);
    return null;
  }

  private async syncAvatarFromPlatform(
    conv: { id: string; fanpageUserId?: string; fanpageId?: string; customerAvatar?: string | null }
  ): Promise<string | null> {
    let avatar: string | null = null;
    try {
      avatar = (await this.syncCustomerData(conv))?.avatar ?? null;
      return avatar;
    } finally {
      this._refreshingBrokenAvatars.delete(conv.id);
      // BE không có avatar mới (hoặc trả lại đúng URL đang lỗi) → nhớ lượt thử cho URL này.
      if (!avatar || avatar === conv.customerAvatar) this.rememberBrokenAvatarAttempt(conv);
    }
  }

  /** Lượt thử lấy lại avatar lỗi gần nhất theo hội thoại + URL avatar. */
  private readonly brokenAvatarAttempts = new Map<string, number>();

  private brokenAvatarKey(conv: { id: string; customerAvatar?: string | null }): string {
    return `${conv.id}|${this.normalizeAvatarForCache(conv.customerAvatar)}`;
  }

  private rememberBrokenAvatarAttempt(conv: { id: string; customerAvatar?: string | null }): void {
    const now = Date.now();
    if (this.brokenAvatarAttempts.size > 500) {
      for (const [key, time] of this.brokenAvatarAttempts) {
        if (now - time >= BROKEN_AVATAR_RETRY_DELAY) this.brokenAvatarAttempts.delete(key);
      }
    }
    this.brokenAvatarAttempts.set(this.brokenAvatarKey(conv), now);
  }
}
